import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useAuth } from '../../contexts/AuthContext';

/**
 * The height Meta's preview page needs to show its cookie notice and a phone screen whole. In a
 * 412x860 window both fitted; on a phone the dialog leaves Meta about 450px, its notice ran off
 * the bottom with its buttons, and touch scrolling inside another site's frame did nothing
 * (reported 2026-10-10). So on a phone the frame is laid out at this height and scaled down to
 * fit: all of it visible at once, and taps still land where they look.
 */
export const META_PAGE_HEIGHT = 860;

/** The frame's layout size and scale for a box of the given size. */
export const fitFrame = (boxWidth, boxHeight) => {
  const scale = boxHeight > 0 ? Math.min(1, boxHeight / META_PAGE_HEIGHT) : 1;
  return { scale, width: Math.round(boxWidth / scale), height: scale < 1 ? META_PAGE_HEIGHT : boxHeight };
};

/**
 * Renders Meta's real WhatsApp Flow preview for the *current saved flow*.
 *
 * The flow is previewed read-only via its real Meta flow id, no throwaway flow is created and no
 * JSON is uploaded (so previewing never reverts a published flow to draft). The builder only mounts
 * this once the flow is SYNCED + VALID, so what Meta renders matches what the user is editing.
 */
const WhatsAppFlowPreview = ({ flowDbId, compact = false }) => {
  const { selectedEnvironment } = useEnvironment();
  const { authenticatedFetch } = useAuth();
  const [baseUrl, setBaseUrl] = useState(null);
  const [interactiveUrl, setInteractiveUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [interactiveMode, setInteractiveMode] = useState(true);
  const [isEmbeddable, setIsEmbeddable] = useState(true);
  const frameBox = useRef(null);
  const [box, setBox] = useState(null);

  const loadPreview = useCallback(async () => {
    if (!selectedEnvironment?.id || !flowDbId) {
      setError('Save the flow before previewing it.');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `/api/environments/${selectedEnvironment.id}/flows/${flowDbId}/preview`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || data.message || 'Failed to generate preview');
      }

      const data = await response.json();
      if (!data.previewUrl) {
        throw new Error('No preview URL returned from the server');
      }
      setBaseUrl(data.previewUrl);
      // interactiveUrl carries Meta's flow_action params; falls back to the static URL if absent.
      setInteractiveUrl(data.interactiveUrl || data.previewUrl);
      setIsEmbeddable(data.embeddable !== false);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedEnvironment?.id, flowDbId, authenticatedFetch]);

  // Load the preview the moment the dialog opens, no extra "Generate" click.
  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  // The phone layout scales the frame to whatever room the dialog leaves it.
  useEffect(() => {
    const el = frameBox.current;
    if (!compact || !el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setBox((prev) => (prev && prev.width === width && prev.height === height ? prev : { width, height }));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [compact, baseUrl, isEmbeddable]);

  const displayUrl = interactiveMode ? (interactiveUrl || baseUrl) : baseUrl;
  const openInNewTab = () => {
    if (displayUrl) window.open(displayUrl, '_blank');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* On a phone every pixel of height goes to Meta's frame, which is scaled to fit it; the
          preview stays interactive, and a failed load still has its "Try again". */}
      {!compact && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={interactiveMode}
              onChange={(e) => setInteractiveMode(e.target.checked)}
              size="small"
            />
          }
          label="Interactive"
        />

        {/* The new tab is desktop only: a phone logged in to Facebook opens Meta's preview link
            in Business Suite, which asks for a Facebook Page instead (reported 2026-10-10). */}
        {baseUrl && (
          <>
            <Tooltip title="Open in new tab">
              <IconButton onClick={openInNewTab} size="small">
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh preview">
              <span>
                <IconButton onClick={loadPreview} size="small" disabled={isLoading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>
      )}

      {displayUrl && isEmbeddable && !compact && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
          This is Meta&apos;s own preview. If it asks about cookies first, accept it there, or open it in a new tab with the button above.
        </Typography>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ m: 2 }}
          action={
            <Button color="inherit" size="small" onClick={loadPreview} disabled={isLoading}>
              Try again
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {isLoading && !displayUrl && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading preview…
          </Typography>
        </Box>
      )}

      {displayUrl && isEmbeddable && (
        <Box ref={frameBox} sx={{ position: 'relative', flex: 1, minHeight: 0, bgcolor: 'grey.50', overflow: 'hidden' }}>
          {isLoading && (
            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          <iframe
            key={displayUrl}
            src={displayUrl}
            title="WhatsApp Flow Preview"
            data-testid="meta-preview-frame"
            style={compact && box
              ? (() => {
                const fit = fitFrame(box.width, box.height);
                return {
                  width: fit.width, height: fit.height, border: 0, display: 'block',
                  transform: `scale(${fit.scale})`, transformOrigin: 'top left',
                };
              })()
              : { width: '100%', height: '100%', border: 0, display: 'block' }}
          />
        </Box>
      )}

      {displayUrl && !isEmbeddable && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 4,
          }}
        >
          <Alert severity="info" sx={{ maxWidth: 500 }}>
            <Typography variant="body2">
              Meta's preview cannot be embedded here due to its security restrictions. Open it in a
              new browser tab instead.
            </Typography>
          </Alert>
          <Button
            variant="contained"
            size="large"
            startIcon={<OpenInNewIcon />}
            onClick={openInNewTab}
            sx={{ minWidth: 250 }}
          >
            Open Preview in Meta
          </Button>
        </Box>
      )}

      {!selectedEnvironment && (
        <Alert severity="info" sx={{ m: 2 }}>
          Select an environment with a connected WABA to preview this flow.
        </Alert>
      )}
    </Box>
  );
};

export default WhatsAppFlowPreview;
